import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { RecipeService } from '@services/recipe/recipe.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { MatDialog } from '@angular/material/dialog';
import { TextPopinComponent } from '@shared/components/text-popin/text-popin.component';

export const recipeStatusGuardFn: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> => {
  const recipeService = inject(RecipeService);
  const router = inject(Router);
  const dialog = inject(MatDialog);

  const recipeFid = route.paramMap.get('recipeId');
  const recipeSourceSystem = route.paramMap.get('recipeType');
  const version = route.paramMap.get('version');
  const type = route.paramMap.get('type');

  if (!recipeFid || !recipeSourceSystem) {
    return of(router.createUrlTree(['/404']));
  }

  // Construct the recipe ID path based on the presence of version and type
  // needed for recipes with /v1/variant ids
  const recipeIdPath = version && type ? encodeURIComponent(`/${version}/${type}/${recipeFid}/`) : recipeFid;

  return recipeService.getRecipe(`${recipeSourceSystem}/${recipeIdPath}`).pipe(
    switchMap((recipe: RecipeLite | undefined) => {
      if (recipe?.status === 'WAITING_FOR_TRANSLATION') {
        const recipeTitle = recipe.title ?? '';
        const topRecipeId = recipe.topRecipeId ?? '';
        //Open modal and redirect when it is closed
        const dialogRef = dialog.open(TextPopinComponent, {
          width: 'auto',
          height: 'auto',
          data: {
            title: 'RECIPE_TRANSLATION.POPIN.HEADER',
            titleParams: { recipeName: recipeTitle },
            text: 'RECIPE_TRANSLATION.POPIN.HTML',
            validateBtn: 'RECIPE_TRANSLATION.POPIN.BTN_LABEL',
          },
        });

        return dialogRef.afterClosed().pipe(
          map((hasAccepted) => {
            const urlTree = hasAccepted ? ['/recipes/edit/translation', topRecipeId] : ['recipes/search'];

            return router.createUrlTree(urlTree);
          })
        );
      }
      return of(true);
    }),
    catchError(() => of(router.createUrlTree(['/404'])))
  );
};
